import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { generateReportPDF } from "../lib/pdf";
import { getForm } from "../lib/formTemplates";
import { BRAND } from "../lib/brand";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SharedReport() {
  const { shareId } = useParams();
  const [state, setState] = useState({ loading: true, report: null });
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api
      .get(`/public/reports/${shareId}`)
      .then(({ data }) => setState({ loading: false, report: data }))
      .catch(() => setState({ loading: false, report: null }));
  }, [shareId]);

  if (state.loading)
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );

  if (!state.report)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 text-center">
        <h1 className="font-heading text-xl font-bold text-slate-900">Report not found</h1>
        <p className="text-sm text-slate-500">This link may have expired.</p>
      </div>
    );

  const r = state.report;
  const formDef = getForm(r.form_type);
  const customer = { name: r._customer_name };
  // shared payload is the raw report; pull embedded snapshot fields where available
  const ctx = {
    report: r,
    customer: r.customer || { name: r.customer_name },
    site: r.site || { name: r.site_name, address: r.site_address },
    pump: r.pump || {},
  };

  const download = async () => {
    setDownloading(true);
    try {
      await generateReportPDF(ctx);
    } catch (e) {
      toast.error("Could not generate PDF");
    }
    setDownloading(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <img src={BRAND.logoMark} alt="" className="h-10 w-10 rounded-full" />
          <div>
            <div className="font-heading text-sm font-extrabold text-slate-900">{BRAND.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-primary">{BRAND.tagline}</div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-4 px-4 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-xl font-extrabold text-slate-900">{formDef.pdfTitle}</h1>
            <p className="text-sm text-slate-500">{r.service_date} • {r.engineer_name}</p>
          </div>
          <Badge className="bg-primary/10 text-primary" variant="secondary">{r.status}</Badge>
        </div>

        <Button onClick={download} disabled={downloading} className="h-12 w-full" data-testid="shared-download-pdf">
          {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
          Download PDF
        </Button>

        {formDef.sections.map((sec) => {
          const rows = sec.fields
            .map((f) => {
              const v = r.data?.[f.id];
              if (f.type === "check") return [f.label, v ? "Yes" : "No"];
              if (v === undefined || v === "" || v === null) return null;
              return [f.label + (f.unit ? ` (${f.unit})` : ""), String(v)];
            })
            .filter(Boolean);
          if (!rows.length) return null;
          return (
            <Card key={sec.id} className="p-4">
              <h3 className="mb-2 font-heading text-sm font-bold text-primary">{sec.title}</h3>
              <dl className="space-y-1.5">
                {rows.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-slate-100 py-1 text-sm">
                    <dt className="text-slate-500">{k}</dt>
                    <dd className="text-right font-medium text-slate-800">{v}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          );
        })}

        {(r.follow_up_actions || r.parts_required || r.final_status) && (
          <Card className="p-4">
            <h3 className="mb-2 font-heading text-sm font-bold text-primary">Outcome</h3>
            <dl className="space-y-1.5 text-sm">
              {r.final_status && <Row k="Final status" v={r.final_status} />}
              {r.follow_up_actions && <Row k="Follow-up actions" v={r.follow_up_actions} />}
              {r.parts_required && <Row k="Parts required" v={r.parts_required} />}
            </dl>
          </Card>
        )}

        <p className="pt-4 text-center text-xs text-slate-400">{BRAND.website}</p>
      </main>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-1">
      <dt className="text-slate-500">{k}</dt>
      <dd className="text-right font-medium text-slate-800">{v}</dd>
    </div>
  );
}
